import { useState } from 'react';
import { toast } from 'sonner';
import { Reveal } from '@/components/motion';
import { PhoneCall, EnvelopeSimple, MapPin, PaperPlaneRight, Clock } from '@phosphor-icons/react';
import { public_api } from '@/lib/api';
import { CONTACT, SERVICES } from '@/data/site';
import { useContent } from '@/context/ContentContext';

export default function Contact() {
  const c = useContent();
  const phone = c('contact.phone', CONTACT.phone_display);
  const email = c('contact.email', CONTACT.email);
  const hours = c('contact.hours', CONTACT.hours);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', subject: '', service_interest: '', message: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await public_api.enquiry({ ...form, source: 'contact_page' });
      setDone(true);
      toast.success("Thanks — we'll be in touch within one business day.");
    } catch (err) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally { setBusy(false); }
  };

  return (
    <section className="pt-16 md:pt-24 pb-24" data-testid="contact-page">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="overline mb-6">Contact us</div>
            <h1 className="h-display text-balance">Talk to a real person, right now.</h1>
            <p className="body-lg mt-6 max-w-md text-pretty">
              Send us a message below or call our 24/7 dispatch line — either way, a coordinator will be with you fast.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            <a href={`tel:${CONTACT.phone_dial}`} className="flex items-start gap-4 p-5 rounded-2xl bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 transition">
              <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <PhoneCall size={20} weight="duotone" className="text-brand" />
              </div>
              <div>
                <div className="font-semibold text-ink" data-testid="contact-phone-display">{phone}</div>
                <div className="text-sm text-ink-muted">Call us anytime — 24/7 dispatch</div>
              </div>
            </a>
            <a href={`mailto:${email}`} className="flex items-start gap-4 p-5 rounded-2xl bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 transition">
              <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <EnvelopeSimple size={20} weight="duotone" className="text-brand" />
              </div>
              <div>
                <div className="font-semibold text-ink" data-testid="contact-email-display">{email}</div>
                <div className="text-sm text-ink-muted">General enquiries and bookings</div>
              </div>
            </a>
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-neutral-50 border border-neutral-200">
              <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <Clock size={20} weight="duotone" className="text-brand" />
              </div>
              <div>
                <div className="font-semibold text-ink" data-testid="contact-hours-display">{hours}</div>
                <div className="text-sm text-ink-muted">Including public holidays</div>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-neutral-50 border border-neutral-200">
              <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <MapPin size={20} weight="duotone" className="text-brand" />
              </div>
              <div>
                <div className="font-semibold text-ink">{CONTACT.hq}</div>
                <div className="text-sm text-ink-muted">Nationally serving all Australian states and territories</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="p-8 md:p-10 rounded-3xl bg-neutral-50 border border-neutral-200">
              {done ? (
                <div className="text-center py-8" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-6">
                    <PaperPlaneRight size={28} weight="fill" className="text-brand" />
                  </div>
                  <h3 className="font-display font-bold text-2xl text-ink">Message received.</h3>
                  <p className="body-md mt-3 max-w-md mx-auto">
                    A coordinator will be in touch within one business day. For urgent transport requests,
                    please call us directly on {phone}.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="label-base">Full name</label>
                      <input required value={form.full_name} onChange={set('full_name')} data-testid="contact-name" className="input-base" placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label className="label-base">Phone</label>
                      <input required value={form.phone} onChange={set('phone')} data-testid="contact-phone" className="input-base" placeholder="04XX XXX XXX" />
                    </div>
                  </div>
                  <div>
                    <label className="label-base">Email</label>
                    <input required type="email" value={form.email} onChange={set('email')} data-testid="contact-email" className="input-base" placeholder="you@email.com.au" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="label-base">Subject (optional)</label>
                      <input value={form.subject} onChange={set('subject')} data-testid="contact-subject" className="input-base" placeholder="e.g. Weekly transport for my mother" />
                    </div>
                    <div>
                      <label className="label-base">Service interest</label>
                      <select value={form.service_interest} onChange={set('service_interest')} data-testid="contact-service" className="input-base">
                        <option value="">Any / not sure</option>
                        {SERVICES.map(s => <option key={s.slug} value={s.name}>{s.name}</option>)}
                        <option value="Careers">Careers</option>
                        <option value="Partnerships">Partnerships</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="label-base">Message</label>
                    <textarea required rows={5} value={form.message} onChange={set('message')} data-testid="contact-message" className="input-base resize-none" placeholder="Tell us a little about what you need — who's travelling, when, and where." />
                  </div>
                  <div className="pt-3">
                    <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-primary w-full md:w-auto">
                      {busy ? 'Sending…' : 'Send message'} <PaperPlaneRight size={16} weight="fill" />
                    </button>
                    <p className="text-xs text-ink-muted mt-4">By submitting you agree to our Privacy Policy. We never share your details with third parties.</p>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
